import type { Font, FontWeight, FontSize, FontStyle, FontVariantCSS2, SystemFont } from './types'
import { Spx } from '@wix/thunderbolt-becky-types'
import {
	lineHeightConverter,
	keyWordConverter,
	numberConverter,
	basicStyleConvertersMap,
	mathFunctionConverter,
} from '../common/commonPropsToCss'
import type { ListOfStrings } from '../common/type'
import _ from 'lodash'

const isKeyword = (propType: string) => propType === 'Keyword'

export const isSystemFont = (prop: Font): prop is SystemFont => isKeyword(prop.type)

export const isNumber = (prop: any): prop is Number => prop.type === 'number'

const fontWeightConverter = (weight: FontWeight): string => {
	return fontWeightTypeToConverter[weight.type](weight)
}

const fontSizeConverter = (size: FontSize, spx?: Spx): string => {
	return fontSizeTypeToConverter[size.type](size, spx)
}

const fontStyleConverter = (style: FontStyle): string => {
	if (style.value === 'oblique') {
		return `oblique ${style.angle.value}${style.angle.type}`
	}
	return style.value
}

const fontVariantConverter = (variant?: FontVariantCSS2): string => {
	return variant ? keyWordConverter(variant) : 'normal'
}

const fontFamilyConverter = (family: ListOfStrings): string => {
	return family.value.map((f) => (f.includes(' ') ? `'${f}'` : f)).join(', ')
}

const fontConverter = (styleProp: Font, spx?: Spx) => {
	if (isSystemFont(styleProp)) {
		return { font: styleProp.value }
	}

	const { style, variant, weight, size, lineHeight, family } = styleProp

	const convertedSize = size && fontSizeConverter(size, spx)
	const convertedStyle = style && fontStyleConverter(style)
	const convertedVariant = fontVariantConverter(variant)
	const convertedWeight = weight && fontWeightConverter(weight)
	const convertedLineHeight = lineHeight && lineHeightConverter(lineHeight, spx)
	const convertedFamily = family && fontFamilyConverter(family)

	const fontCompleteOverride = {
		style: convertedStyle,
		variant: convertedVariant,
		weight: convertedWeight,
		size: convertedSize,
		lineHeight: convertedLineHeight,
		family: convertedFamily,
		font:
			'var(--font-style), var(--font-variant), var(--font-weight), var(--font-size)/var(--font-lineHeight) var(--font-family)',
	}
	return Object.fromEntries(
		Object.entries(fontCompleteOverride).filter(([, value]) => value !== undefined)
	) as Record<string, string>
}

const fontSizeTypeToConverter = _.mergeWith(
	{
		Keyword: keyWordConverter,
		number: numberConverter,
		MathFunction: mathFunctionConverter,
	},
	{ ...basicStyleConvertersMap }
) as Record<string, (size: FontSize, spx?: Spx) => string>

const fontWeightTypeToConverter = {
	Keyword: keyWordConverter,
	number: numberConverter,
} as Record<string, (weight: FontWeight) => string>

export {
	fontConverter,
	fontSizeConverter,
	fontStyleConverter,
	fontVariantConverter,
	fontFamilyConverter,
	fontWeightConverter,
}
